import { createContext, ReactNode, useContext, useMemo } from 'react';

/**
 * Configuration for the client used by framework data hooks.
 */
export interface PericlesClientConfig {
  baseUrl?: string;
  fetcher?: (input: RequestInfo, init?: RequestInit) => Promise<Response>;
}

/**
 * Props for the provider wrapping React trees that rely on framework data hooks.
 */
export interface PericlesProviderProps extends PericlesClientConfig {
  children: ReactNode;
}

const defaultFetcher = (input: RequestInfo, init?: RequestInit) => fetch(input, init);

// TODO: Extend the client context with user session metadata when available.
// TODO: Surface UI permissions within this provider for downstream consumers.
const PericlesContext = createContext<Required<PericlesClientConfig> | null>(null);

/**
 * Provides fetch configuration (baseUrl, fetcher) to descendant hooks and components.
 */
export function PericlesProvider({ baseUrl = '', fetcher = defaultFetcher, children }: PericlesProviderProps): JSX.Element {
  const value = useMemo(
    () => ({
      baseUrl,
      fetcher,
    }),
    [baseUrl, fetcher]
  );

  return <PericlesContext.Provider value={value}>{children}</PericlesContext.Provider>;
}

/**
 * Access the configured Pericles client; throws if used outside a provider.
 */
export function usePericlesClient(): Required<PericlesClientConfig> {
  const context = useContext(PericlesContext);

  if (!context) {
    throw new Error('usePericlesClient must be used within a PericlesProvider');
  }

  return context;
}
